import api from '../../helpers/apirest/api';

export const state = {
    // currentUser: localStorage.getItem('currentUser')
    currentUser: JSON.parse(localStorage.getItem('currentUser')) || null,
    currentToken: localStorage.getItem('currentToken') || null,
    tempToken: null, // Token temporal para verificación 2FA
    ssoParams: null, // Parámetros SSO detectados en la URL
    isLoggingOut: false, // Estado de logout en progreso
}

export const mutations = {
    SET_CURRENT_USER(state, newValue) {
        state.currentUser = newValue
        saveState('currentUser', newValue, true)
    },
    SET_CURRENT_TOKEN(state, token) {
        state.currentToken = token
        saveState('currentToken', token, false)
    },
    SET_TEMP_TOKEN(state, token) {
        state.tempToken = token
    },
    SET_SSO_PARAMS(state, params) {
        state.ssoParams = params
    },
    CLEAR_TEMP_DATA(state) {
        state.tempToken = null
    },
    SET_LOGGING_OUT(state, value) {
        state.isLoggingOut = value
    }
}

export const getters = {
    // Whether the user is currently logged in.
    loggedIn(state) {
        return !!state.currentUser
    },
    // Whether we have a temporary token for 2FA verification
    hasTempToken(state) {
        return !!state.tempToken
    },
    // Whether we're in SSO context
    isSSO(state) {
        return !!state.ssoParams
    }
}

export const actions = {

    async login({ commit }, { email, password, client_id, redirect_uri, state, referer_url } = {}) {
        const response = await api.post({
            baseURL: process.env.VUE_APP_API_URL,
            url: '/auth/login',
            data:{
                email,
                password,
                client_id,
                redirect_uri,
                state,
                referer_url
            },
            config: {
                withLoading: true
            }
        });

        const responseData = response?.data?.data;

        // Handle different login scenarios
        if (responseData?.requires_2fa_setup) {
            // User needs to configure 2FA (required by admin)
            commit('SET_TEMP_TOKEN', responseData.temp_token);
            return {
                success: false,
                requires_2fa_setup: true,
                temp_token: responseData.temp_token,
                message: responseData.message
            };
        } else if (responseData?.requires_2fa) {
            // User needs 2FA verification
            commit('SET_TEMP_TOKEN', responseData.temp_token);
            return {
                success: false,
                requires_2fa: true,
                temp_token: responseData.temp_token,
                message: responseData.message
            };
        } else {
            // Successful login without 2FA
            commit('SET_CURRENT_USER', responseData?.user);
            commit('SET_CURRENT_TOKEN', responseData?.token);
            commit('CLEAR_TEMP_DATA');
            return responseData;
        }
    },

    async verify2FA({ commit, state }, { code, recovery_code, client_id, redirect_uri, state: state2, referer_url } = {}) {
        if (!state.tempToken) {
            throw new Error('No temporary token available for 2FA verification');
        }

        const response = await api.post({
            url: 'auth/2fa/verify',
            data: {
                code: code || null,
                recovery_code: recovery_code || null,
                client_id: client_id || null,
                redirect_uri: redirect_uri || null,
                state: state2 || null,
                referer_url: referer_url || null
            },
            config: {
                withLoading: true,
                headers: {
                    'Authorization': `Bearer ${state.tempToken}`
                }
            }
        });

        const data = response?.data?.data;
        const user = data?.user;
        commit('SET_CURRENT_USER', user);
        commit('SET_CURRENT_TOKEN', data?.token);
        commit('CLEAR_TEMP_DATA');

        return data;
    },

    async enable2FA({ getters, state }) {
        // Allow enabling with temp token (during login) or when logged in
        if (!getters.loggedIn && !state.tempToken) {
            throw new Error('User must be logged in or have a temp token to enable 2FA');
        }

        const config = {
            withLoading: true
        };

        // Add temp token to headers if user is not fully logged in
        if (!getters.loggedIn && state.tempToken) {
            config.headers = {
                'Authorization': `Bearer ${state.tempToken}`
            };
        }

        const response = await api.post({
            url: 'auth/2fa/enable',
            config: config
        });

        return response?.data?.data;
    },

    async confirm2FA({ getters, commit, state }, { code } = {}) {
        // Allow confirmation with temp token (during login) or when logged in
        if (!getters.loggedIn && !state.tempToken) {
            throw new Error('User must be logged in or have a temp token to confirm 2FA');
        }

        const config = {
            withLoading: true
        };

        // Add temp token to headers if user is not fully logged in
        if (!getters.loggedIn && state.tempToken) {
            config.headers = {
                'Authorization': `Bearer ${state.tempToken}`
            };
        }

        // Prepare data with SSO params if available
        const requestData = {
            code: code
        };

        // Add SSO params if they exist (for SSO flow during 2FA setup)
        if (state.ssoParams) {
            if (state.ssoParams.client_id) requestData.client_id = state.ssoParams.client_id;
            if (state.ssoParams.redirect_uri) requestData.redirect_uri = state.ssoParams.redirect_uri;
            if (state.ssoParams.state) requestData.state = state.ssoParams.state;
            if (state.ssoParams.referer_url) requestData.referer_url = state.ssoParams.referer_url;
        }

        const response = await api.post({
            url: 'auth/2fa/confirm',
            data: requestData,
            config: config
        });

        // If user was using temp token, they should now be fully logged in
        if (!getters.loggedIn && state.tempToken && response?.data?.data?.user) {
            commit('SET_CURRENT_USER', response.data.data.user);
            commit('SET_CURRENT_TOKEN', response.data.data.token);
            commit('CLEAR_TEMP_DATA');
        }

        return response?.data?.data;
    },

    async disable2FA({ getters }, { password } = {}) {
        if (!getters.loggedIn) {
            throw new Error('User must be logged in to disable 2FA');
        }

        const response = await api.post({
            url: 'auth/2fa/disable',
            data: {
                password: password
            },
            config: {
                withLoading: true
            }
        });

        return response?.data?.data;
    },

    async get2FAStatus({ getters }) {
        if (!getters.loggedIn) {
            throw new Error('User must be logged in to get 2FA status');
        }

        const response = await api.get({
            url: 'auth/2fa/status',
            config: {
                withLoading: true
            }
        });

        return response?.data?.data?.two_factor_status;
    },

    async regenerateRecoveryCodes({ getters }, { password } = {}) {
        if (!getters.loggedIn) {
            throw new Error('User must be logged in to regenerate recovery codes');
        }

        const response = await api.post({
            url: 'auth/2fa/recovery-codes',
            data: {
                password: password
            },
            config: {
                withLoading: true
            }
        });

        return response?.data?.data;
    },

    setSSOParams({ commit }, params) {
        commit('SET_SSO_PARAMS', params);
    },

    clearTempData({ commit }) {
        commit('CLEAR_TEMP_DATA');
    },

    /**
     * OAuth Authorization for already authenticated users
     * Used when external apps try to SSO with already logged in users
     */
    async oauthAuthorize({ getters }, { clientId, redirectUri, state, responseType = 'code', scope = 'read', refererUrl = null } = {}) {
        if (!getters.loggedIn) {
            throw new Error('User must be logged in to authorize OAuth');
        }

        const response = await api.post({
            url: 'auth/oauth/authorize',
            data: {
                client_id: clientId,
                redirect_uri: redirectUri,
                state: state,
                response_type: responseType,
                scope: scope,
                referer_url: refererUrl
            },
            config: {
                withLoading: false // We'll show our own loading in the authorize page
            }
        });

        return response?.data?.data;
    },

    async logout({ commit }, { refererUrl = null } = {}) {
        commit('SET_LOGGING_OUT', true);

        try {
            // Call backend logout API
            const response = await api.post({
                url: 'auth/logout',
                data: refererUrl ? { referer_url: refererUrl } : {},
                config: {
                    withLoading: false // We'll show our own loading in the logout page
                }
            });

            const logoutData = response?.data?.data;

            // Clear local authentication state
            commit('SET_CURRENT_USER', null);
            commit('SET_CURRENT_TOKEN', null);            
            commit('CLEAR_TEMP_DATA');

            return {
                success: true,
                sso_logout_urls: logoutData?.sso_logout_urls || [],
                logout_redirect_url: logoutData?.logout_redirect_url || null,
                referer_url: logoutData?.referer_url || refererUrl,
                message: logoutData?.message
            };

        } catch (error) {
            console.error('Logout error:', error);
            // Clear auth even on error
            commit('SET_CURRENT_USER', null);
            commit('SET_CURRENT_TOKEN', null);
            commit('CLEAR_TEMP_DATA');
            
            return {
                success: false,
                error: error.message,
                sso_logout_urls: [],
                referer_url: refererUrl
            };
        } finally {
            commit('SET_LOGGING_OUT', false);
        }
    },


    async forgotPassword({ getters }, { email } = {}) {
        console.log( getters);

        await api.get({
            baseURL: process.env.VUE_APP_WEB_URL,
            url: '/sanctum/csrf-cookie',
            config: {
                withLoading: true
            }
        });

        await api.save({
            url: 'auth/forgot-password',
            data:{
                email:email
            },
            config: {
                withLoading: true
            }
        });
    },
    
    async validateResetToken({ getters }, { email, token } = {}) {
        console.log( getters);

        await api.get({
            baseURL: process.env.VUE_APP_WEB_URL,
            url: '/sanctum/csrf-cookie',
            config: {
                withLoading: true
            }
        });

        await api.post({
            url: 'auth/validate-reset-token',
            data:{
                token:token,
                email:email
            },
            config: {
                withLoading: true
            }
        });
    },
    
    async resetPassword({ getters }, { token, email, password, re_password } = {}) {
        console.log( getters);

        await api.get({
            baseURL: process.env.VUE_APP_WEB_URL,
            url: '/sanctum/csrf-cookie',
            config: {
                withLoading: true
            }
        });

        await api.post({
            url: 'auth/reset-password',
            data:{
                token:token,
                email:email,
                password:password,
                re_password:re_password,
            },
            config: {
                withLoading: true
            }
        });
    },

    // User management 2FA actions (for administrators)
    async require2FA({ getters }, { userId } = {}) {
        if (!getters.loggedIn) {
            throw new Error('User must be logged in to require 2FA');
        }

        const response = await api.post({
            url: `users/${userId}/2fa/require`,
            config: {
                withLoading: true
            }
        });

        return response?.data;
    },

    async unrequire2FA({ getters }, { userId } = {}) {
        if (!getters.loggedIn) {
            throw new Error('User must be logged in to unrequire 2FA');
        }

        const response = await api.delete({
            url: `users/${userId}/2fa/require`,
            config: {
                withLoading: true
            }
        });

        return response?.data?.data;
    },

    async forceDisable2FA({ getters }, { userId } = {}) {
        if (!getters.loggedIn) {
            throw new Error('User must be logged in to force disable 2FA');
        }

        const response = await api.delete({
            url: `users/${userId}/2fa/force-disable`,
            config: {
                withLoading: true
            }
        });

        return response?.data;
    },

    async getUserTwoFactorStatus({ getters }, { userId } = {}) {
        if (!getters.loggedIn) {
            throw new Error('User must be logged in to get user 2FA status');
        }

        const response = await api.get({
            url: `users/${userId}/2fa`,
            config: {
                withLoading: true
            }
        });

        return response?.data;
    },

    // Account lockout management actions (for administrators)
    async unlockUserAccount({ getters }, { userId } = {}) {
        if (!getters.loggedIn) {
            throw new Error('User must be logged in to unlock accounts');
        }

        const response = await api.post({
            url: 'users/unlock',
            data: {
                user_id: userId
            },
            config: {
                withLoading: true
            }
        });

        return response?.data;
    },

    async getUserLockoutStatus({ getters }, { userId } = {}) {
        if (!getters.loggedIn) {
            throw new Error('User must be logged in to get lockout status');
        }

        const response = await api.get({
            url: `users/${userId}/lockout-status`,
            config: {
                withLoading: true
            }
        });

        return response?.data;
    }
}

// ===
// Private helpers
// ===

function saveState(key, state, isJson = true) {
    if (state) {
        localStorage.setItem(key, isJson ? JSON.stringify(state) : state);
    } else {
        localStorage.removeItem(key);
    }
}
